/**
 * Advanced NURBS/Surface Tools
 * 
 * 3D-style surface creation and solid operations:
 * - Loft (surface from curves)
 * - Sweep (curve along rail)
 * - Revolve (curve around axis)
 * - Boolean operations (Union, Difference, Intersection)
 * - Extrude surface
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import React from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useAppStore, type Entity } from '../../state/store';
import { generateId } from '../../utils/helpers';
import { GROUND_PLANE } from '../../core/scene';
import { notify } from '../../utils/notify';
import { SolidKernel } from '../../core/SolidKernel';

// ============================================================================
// LOFT TOOL
// ============================================================================

export interface LoftData {
    curves: string[];       // IDs of source curves
    controlPoints: number[][][]; // Control points from each curve
    closed: boolean;        // Closed loft (loop back to first curve)
    segments: number;       // Subdivisions between curves
}

/**
 * Loft Tool Hook
 * 
 * Select multiple curves, then create surface through them
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useLoftTool() {
    const [selectedCurves, setSelectedCurves] = useState<string[]>([]);
    const [isClosed, setIsClosed] = useState(false);

    const addEntity = useAppStore((s) => s.addEntity);
    const entities = useAppStore((s) => s.entities);
    const selectedIds = useAppStore((s) => s.selectedIds);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const isActive = activeTool === 'loft';

    const addCurveToLoft = useCallback((curveId: string) => {
        const entity = entities[curveId];
        if (entity && (entity.type === 'curve' || entity.type === 'polyline' || entity.type === 'line')) {
            if (!selectedCurves.includes(curveId)) {
                setSelectedCurves([...selectedCurves, curveId]);
            }
        }
    }, [entities, selectedCurves]);

    const removeCurveFromLoft = useCallback((curveId: string) => {
        setSelectedCurves(selectedCurves.filter(id => id !== curveId));
    }, [selectedCurves]);

    const createLoft = useCallback(() => {
        if (selectedCurves.length < 2) return;

        // Extract control points from each curve
        const controlPoints: number[][][] = selectedCurves.map(id => {
            const entity = entities[id];
            if (!entity) return [];

            if (entity.type === 'curve') {
                return entity.data.points as number[][];
            } else if (entity.type === 'polyline') {
                return entity.data.vertices as number[][];
            } else if (entity.type === 'line') {
                return [entity.data.start as number[], entity.data.end as number[]];
            }
            return [];
        });

        addEntity({
            id: generateId(),
            type: 'loft',
            name: `Loft-${Date.now()}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                curves: selectedCurves,
                controlPoints,
                closed: isClosed,
                segments: 16,
            } as LoftData,
        });

        setSelectedCurves([]);
    }, [selectedCurves, entities, isClosed, addEntity, activeLayerId]);

    const cancelLoft = useCallback(() => {
        setSelectedCurves([]);
    }, []);

    // Auto-add selected entities as curves
    useEffect(() => {
        if (isActive && selectedIds.length > 0) {
            selectedIds.forEach(id => addCurveToLoft(id));
        }
    }, [isActive, selectedIds, addCurveToLoft]);

    // Keyboard handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.key === 'Escape') {
                cancelLoft();
            } else if (e.key === 'Enter') {
                createLoft();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive, cancelLoft, createLoft]);

    return {
        isActive,
        selectedCurves,
        isClosed,
        setIsClosed,
        addCurveToLoft,
        removeCurveFromLoft,
        createLoft,
        cancelLoft,
    };
}

/**
 * Loft Surface Mesh
 */
export const LoftMesh: React.FC<{
    data: LoftData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: (e: ThreeEvent<MouseEvent>) => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#4488ff';

    const geometry = useMemo(() => {
        if (data.controlPoints.length < 2) return null;

        // Create lofted geometry using linear interpolation between curves
        const curves = data.controlPoints;
        // const segments = data.segments;
        const positions: number[] = [];
        const indices: number[] = [];

        // Normalize point count across curves (simple approach)
        const maxPoints = Math.max(...curves.map(c => c.length));

        for (let i = 0; i < curves.length; i++) {
            const curve = curves[i];
            for (let j = 0; j < maxPoints; j++) {
                const t = curve.length > 1 ? j / (maxPoints - 1) : 0;
                const idx = Math.min(Math.floor(t * (curve.length - 1)), curve.length - 1);
                const pt = curve[idx] || curve[curve.length - 1];
                positions.push(pt[0], pt[1], pt[2]);
            }
        }

        // Create faces
        for (let i = 0; i < curves.length - 1; i++) {
            for (let j = 0; j < maxPoints - 1; j++) {
                const a = i * maxPoints + j;
                const b = a + 1;
                const c = (i + 1) * maxPoints + j;
                const d = c + 1;

                indices.push(a, c, b);
                indices.push(b, c, d);
            }
        }

        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geo.setIndex(indices);
        geo.computeVertexNormals();

        return geo;
    }, [data]);

    if (!geometry) return null;

    return (
        <mesh
            geometry={geometry}
            onClick={(e) => { e.stopPropagation(); onClick(e); }}
        >
            <meshStandardMaterial
                color={color}
                side={THREE.DoubleSide}
                roughness={0.5}
            />
        </mesh>
    );
};

// ============================================================================
// SWEEP TOOL
// ============================================================================

export interface SweepData {
    profile: string;        // Profile curve ID
    rail: string;           // Rail curve ID
    profilePoints: number[][];
    railPoints: number[][];
    twist: number;          // Twist angle along path
    scale: number;          // End scale factor
}

/**
 * Sweep Tool Hook
 * 
 * Select profile curve, then rail curve
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSweepTool() {
    const [profileId, setProfileId] = useState<string | null>(null);
    const [railId, setRailId] = useState<string | null>(null);

    const addEntity = useAppStore((s) => s.addEntity);
    const entities = useAppStore((s) => s.entities);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const isActive = activeTool === 'sweep';

    const selectCurve = useCallback((curveId: string) => {
        const entity = entities[curveId];
        if (!entity || !['curve', 'polyline', 'line', 'circle'].includes(entity.type)) {
            return;
        }

        if (!profileId) {
            setProfileId(curveId);
        } else if (!railId) {
            setRailId(curveId);
        }
    }, [entities, profileId, railId]);

    const getPointsFromEntity = useCallback((id: string): number[][] => {
        const entity = entities[id];
        if (!entity) return [];

        switch (entity.type) {
            case 'curve':
                return entity.data.points as number[][];
            case 'polyline':
                return entity.data.vertices as number[][];
            case 'line':
                return [entity.data.start as number[], entity.data.end as number[]];
            case 'circle': {
                const center = entity.data.center as number[];
                const radius = entity.data.radius as number;
                const pts: number[][] = [];
                for (let i = 0; i <= 32; i++) {
                    const a = (i / 32) * Math.PI * 2;
                    pts.push([
                        center[0] + Math.cos(a) * radius,
                        center[1],
                        center[2] + Math.sin(a) * radius
                    ]);
                }
                return pts;
            }
            default:
                return [];
        }
    }, [entities]);

    const createSweep = useCallback(() => {
        if (!profileId || !railId) return;

        const profilePoints = getPointsFromEntity(profileId);
        const railPoints = getPointsFromEntity(railId);

        if (profilePoints.length < 2 || railPoints.length < 2) return;

        addEntity({
            id: generateId(),
            type: 'sweep',
            name: `Sweep-${Date.now()}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                profile: profileId,
                rail: railId,
                profilePoints,
                railPoints,
                twist: 0,
                scale: 1,
            } as SweepData,
        });

        setProfileId(null);
        setRailId(null);
    }, [profileId, railId, getPointsFromEntity, addEntity, activeLayerId]);

    // Auto-create when both are selected
    useEffect(() => {
        if (profileId && railId) {
            createSweep();
        }
    }, [profileId, railId, createSweep]);

    const cancelSweep = useCallback(() => {
        setProfileId(null);
        setRailId(null);
    }, []);

    return {
        isActive,
        profileId,
        railId,
        selectCurve,
        createSweep,
        cancelSweep,
    };
}

// ============================================================================
// REVOLVE TOOL
// ============================================================================

export interface RevolveData {
    profile: string;        // Profile curve ID
    profilePoints: number[][];
    axisStart: number[];    // Axis line start
    axisEnd: number[];      // Axis line end
    startAngle: number;     // Start angle (radians)
    endAngle: number;       // End angle (radians)
    segments: number;       // Angular segments
}

/**
 * Revolve Tool Hook
 * 
 * Select profile curve, then define axis
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useRevolveTool() {
    const [profileId, setProfileId] = useState<string | null>(null);
    const [axisStart, setAxisStart] = useState<THREE.Vector3 | null>(null);
    const [axisEnd, setAxisEnd] = useState<THREE.Vector3 | null>(null);
    const [previewPoint, setPreviewPoint] = useState<THREE.Vector3 | null>(null);

    const addEntity = useAppStore((s) => s.addEntity);
    const entities = useAppStore((s) => s.entities);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera } = useThree();

    const isActive = activeTool === 'revolve';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const getPointsFromEntity = useCallback((id: string): number[][] => {
        const entity = entities[id];
        if (!entity) return [];

        if (entity.type === 'curve') return entity.data.points as number[][];
        if (entity.type === 'polyline') return entity.data.vertices as number[][];
        if (entity.type === 'line') {
            return [entity.data.start as number[], entity.data.end as number[]];
        }
        return [];
    }, [entities]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        if (!axisStart) {
            setAxisStart(point.clone());
        } else if (!axisEnd && profileId) {
            setAxisEnd(point.clone());

            // Create revolve
            const profilePoints = getPointsFromEntity(profileId);
            if (profilePoints.length >= 2) {
                addEntity({
                    id: generateId(),
                    type: 'revolve',
                    name: `Revolve-${Date.now()}`,
                    visible: true,
                    locked: false,
                    layer: activeLayerId,
                    data: {
                        profile: profileId,
                        profilePoints,
                        axisStart: [axisStart.x, axisStart.y, axisStart.z],
                        axisEnd: [point.x, point.y, point.z],
                        startAngle: 0,
                        endAngle: Math.PI * 2,
                        segments: 32,
                    } as RevolveData,
                });
            }

            setProfileId(null);
            setAxisStart(null);
            setAxisEnd(null);
        }
    }, [isActive, axisStart, axisEnd, profileId, getGroundPoint, getPointsFromEntity, addEntity, activeLayerId]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            setPreviewPoint(point);
        }
    }, [isActive, getGroundPoint]);

    const selectProfile = useCallback((curveId: string) => {
        const entity = entities[curveId];
        if (entity && ['curve', 'polyline', 'line'].includes(entity.type)) {
            setProfileId(curveId);
        }
    }, [entities]);

    return {
        isActive,
        profileId,
        axisStart,
        previewPoint,
        selectProfile,
        handlePointerDown,
        handlePointerMove,
    };
}

// ============================================================================
// BOOLEAN OPERATIONS
// ============================================================================

export type BooleanOperation = 'union' | 'difference' | 'intersection';

export interface BooleanData {
    operation: BooleanOperation;
    operandA: string;       // First solid ID
    operandB: string;       // Second solid ID
    // Result mesh stored directly for rendering (vertices/triangles).
    vertices: number[][];
    indices: number[][];
}

/**
 * Convert an operand entity into a plain world-space THREE.Mesh so the
 * shared SolidKernel (three-bvh-csg) can evaluate it. Supported operands:
 * box (position+size), solid payloads (vertices+indices from the command
 * engine) and extrude results (profile + distance). Anything else is
 * reported honestly instead of producing an empty ghost entity.
 */
function entityToOperandMesh(entity: Entity): THREE.Mesh | null {
    const d = entity.data ?? {};
    if (entity.type === 'box') {
        const pos = d.position as number[] | undefined;
        const size = d.size as number[] | undefined;
        if (!pos || !size) return null;
        const geo = new THREE.BoxGeometry(size[0], size[1], size[2]);
        const mesh = new THREE.Mesh(geo);
        mesh.position.set(pos[0], pos[1], pos[2]);
        mesh.updateMatrixWorld(true);
        return mesh;
    }
    if (entity.type === 'solid') {
        const vertices = d.vertices as number[][] | undefined;
        const indices = d.indices as number[][] | undefined;
        if (!Array.isArray(vertices) || vertices.length === 0) return null;
        const geo = new THREE.BufferGeometry();
        const pos = new Float32Array(vertices.length * 3);
        vertices.forEach((v, i) => { pos[i * 3] = v[0]; pos[i * 3 + 1] = v[1]; pos[i * 3 + 2] = v[2] ?? 0; });
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        const idx: number[] = [];
        (indices ?? []).forEach((t) => { if (t.length === 3) idx.push(t[0], t[1], t[2]); });
        if (idx.length > 0) geo.setIndex(idx);
        geo.computeVertexNormals();
        const offset = (d.position as number[] | undefined) ?? [0, 0, 0];
        const mesh = new THREE.Mesh(geo);
        mesh.position.set(offset[0], offset[1], offset[2] ?? 0);
        mesh.updateMatrixWorld(true);
        return mesh;
    }
    if (entity.type === 'extrude') {
        const profilePoints = d.profilePoints as number[][] | undefined;
        if (!profilePoints || profilePoints.length < 3) return null;
        const shape = new THREE.Shape();
        shape.moveTo(profilePoints[0][0], profilePoints[0][1]);
        for (let i = 1; i < profilePoints.length; i++) shape.lineTo(profilePoints[i][0], profilePoints[i][1]);
        const geo = new THREE.ExtrudeGeometry(shape, { depth: (d.distance as number) ?? 1, bevelEnabled: false });
        const mesh = new THREE.Mesh(geo);
        mesh.updateMatrixWorld(true);
        return mesh;
    }
    return null;
}

/** Extract a world-space mesh's triangle payload (disposes the geometry). */
function meshToPayload(mesh: THREE.Mesh): { vertices: number[][]; indices: number[][] } {
    const g = mesh.geometry as THREE.BufferGeometry;
    const p = g.getAttribute('position') as THREE.BufferAttribute;
    const vertices: number[][] = [];
    for (let i = 0; i < p.count; i++) vertices.push([p.getX(i), p.getY(i), p.getZ(i)]);
    const indices: number[][] = [];
    const index = g.getIndex();
    if (index) {
        for (let i = 0; i < index.count; i += 3) indices.push([index.getX(i), index.getX(i + 1), index.getX(i + 2)]);
    }
    g.dispose();
    return { vertices, indices };
}

/**
 * Boolean Tool Hook
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useBooleanTool(operation: BooleanOperation = 'union') {
    const [operandA, setOperandA] = useState<string | null>(null);
    const [operandB, setOperandB] = useState<string | null>(null);

    const addEntity = useAppStore((s) => s.addEntity);
    // const deleteEntity = useAppStore((s) => s.deleteEntity);
    const entities = useAppStore((s) => s.entities);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const isActive = activeTool === `boolean${operation.charAt(0).toUpperCase() + operation.slice(1)}`;

    const selectSolid = useCallback((solidId: string) => {
        const entity = entities[solidId];
        if (!entity) return;

        // Only operands with a real world-space mesh participate in CSG.
        if (!entityToOperandMesh(entity)) return;

        if (!operandA) {
            setOperandA(solidId);
        } else if (!operandB && solidId !== operandA) {
            setOperandB(solidId);
        }
    }, [entities, operandA, operandB]);

    const performBoolean = useCallback(() => {
        if (!operandA || !operandB) return;
        const a = entities[operandA];
        const b = entities[operandB];
        const meshA = a ? entityToOperandMesh(a) : null;
        const meshB = b ? entityToOperandMesh(b) : null;
        if (!meshA || !meshB) {
            notify('BOOLEAN: operands have no solid mesh to combine.', 'warning');
            return;
        }

        // Real CSG through the shared SolidKernel (three-bvh-csg): result
        // replaces both operands as one 'solid' entity, undoable as a batch.
        let resultMesh: THREE.Mesh;
        try {
            resultMesh = operation === 'union' ? SolidKernel.union(meshA, meshB)
                : operation === 'difference' ? SolidKernel.subtract(meshA, meshB)
                    : SolidKernel.intersect(meshA, meshB);
        } catch {
            notify('BOOLEAN: CSG failed (non-manifold input).', 'error');
            return;
        }
        const payload = meshToPayload(resultMesh);
        const app = useAppStore.getState();
        const resultId = generateId();
        // Render as a 'solid' entity: SolidEntityMesh draws the CSG result
        // through the shared vertices/indices path in every workbench.
        const resultEntity = {
            id: resultId, type: 'solid', name: `Boolean-${operation}-${Date.now()}`,
            visible: true, locked: false, layer: activeLayerId,
            data: { operation, operandA, operandB, vertices: payload.vertices, indices: payload.indices, color: '#8fb4d9' },
        };

        app.pushHistory({
            type: 'boolean',
            timestamp: Date.now(),
            data: { operandA, operandB },
            undo: () => {
                app.deleteEntity(resultId);
                app.addEntity(a);
                app.addEntity(b);
            },
            redo: () => {
                app.deleteEntity(operandA);
                app.deleteEntity(operandB);
                app.addEntity(resultEntity);
            },
        });

        app.addEntity(resultEntity);
        // Consume the operands: the union/subtract/intersect replaces them.
        app.deleteEntity(operandA);
        app.deleteEntity(operandB);

        setOperandA(null);
        setOperandB(null);
    }, [operandA, operandB, operation, entities, activeLayerId]);

    // Auto-perform when both selected
    useEffect(() => {
        if (operandA && operandB) {
            performBoolean();
        }
    }, [operandA, operandB, performBoolean]);

    const cancelBoolean = useCallback(() => {
        setOperandA(null);
        setOperandB(null);
    }, []);

    return {
        isActive,
        operation,
        operandA,
        operandB,
        selectSolid,
        performBoolean,
        cancelBoolean,
    };
}

// ============================================================================
// EXTRUDE SURFACE TOOL
// ============================================================================

export interface ExtrudeData {
    profile: string;        // Profile curve ID
    profilePoints: number[][];
    direction: number[];    // Extrusion direction vector
    distance: number;       // Extrusion distance
    taperAngle: number;     // Taper angle in degrees
    cap: boolean;           // Cap ends
}

/**
 * Extrude Tool Hook
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useExtrudeTool() {
    const [profileId, setProfileId] = useState<string | null>(null);
    const [direction, setDirection] = useState<THREE.Vector3>(new THREE.Vector3(0, 0, 1));
    const [distance, setDistance] = useState<number>(1);
    // const [previewPoint, setPreviewPoint] = useState<THREE.Vector3 | null>(null);

    const addEntity = useAppStore((s) => s.addEntity);
    const entities = useAppStore((s) => s.entities);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    // const { raycaster, pointer, camera } = useThree();

    const isActive = activeTool === 'extrude';

    const getPointsFromEntity = useCallback((id: string): number[][] => {
        const entity = entities[id];
        if (!entity) return [];

        switch (entity.type) {
            case 'curve':
                return entity.data.points as number[][];
            case 'polyline':
                return entity.data.vertices as number[][];
            case 'rectangle': {
                const c1 = entity.data.corner1 as number[];
                const c2 = entity.data.corner2 as number[];
                return [
                    c1,
                    [c2[0], c1[1], c1[2]],
                    c2,
                    [c1[0], c1[1], c2[2]],
                    c1, // close
                ];
            }
            case 'circle': {
                const center = entity.data.center as number[];
                const radius = entity.data.radius as number;
                const pts: number[][] = [];
                for (let i = 0; i <= 32; i++) {
                    const a = (i / 32) * Math.PI * 2;
                    pts.push([
                        center[0] + Math.cos(a) * radius,
                        center[1],
                        center[2] + Math.sin(a) * radius
                    ]);
                }
                return pts;
            }
            default:
                return [];
        }
    }, [entities]);

    const selectProfile = useCallback((curveId: string) => {
        const entity = entities[curveId];
        if (entity && ['curve', 'polyline', 'rectangle', 'circle', 'polygon'].includes(entity.type)) {
            setProfileId(curveId);
        }
    }, [entities]);

    const createExtrude = useCallback(() => {
        if (!profileId) return;

        const profilePoints = getPointsFromEntity(profileId);
        if (profilePoints.length < 2) return;

        addEntity({
            id: generateId(),
            type: 'extrude',
            name: `Extrude-${Date.now()}`,
            visible: true,
            locked: false,
            layer: activeLayerId,
            data: {
                profile: profileId,
                profilePoints,
                direction: [direction.x, direction.y, direction.z],
                distance,
                taperAngle: 0,
                cap: true,
            } as ExtrudeData,
        });

        setProfileId(null);
    }, [profileId, direction, distance, getPointsFromEntity, addEntity, activeLayerId]);

    // Completion contract (same as loft): Enter extrudes the picked profile,
    // Escape aborts. Without this the tool hung forever after selectProfile.
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;
            if (e.key === 'Escape') {
                setProfileId(null);
            } else if (e.key === 'Enter' && profileId) {
                createExtrude();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive, profileId, createExtrude]);

    return {
        isActive,
        profileId,
        direction,
        distance,
        selectProfile,
        setDirection,
        setDistance,
        createExtrude,
    };
}

/**
 * Sweep Mesh Component (EntityLayer case 'sweep').
 *
 * Sweeps the closed profile ring along the rail by parallel-translating it
 * to every rail point (a lightweight 3D Sweep1 for planar rails). Rings
 * are lofted with triangle strips exactly like LoftMesh, so the result
 * renders in every workbench.
 */
export const SweepMesh: React.FC<{
    data: SweepData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: (e: ThreeEvent<MouseEvent>) => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#cc8844';

    const geometry = useMemo(() => {
        const { profilePoints, railPoints } = data;
        if (profilePoints.length < 2 || railPoints.length < 2) return null;

        // Profile centroid so translation centers the ring on each rail node.
        const cx = profilePoints.reduce((s, p) => s + p[0], 0) / profilePoints.length;
        const cy = profilePoints.reduce((s, p) => s + p[1], 0) / profilePoints.length;
        const cz = profilePoints.reduce((s, p) => s + (p[2] ?? 0), 0) / profilePoints.length;

        const positions: number[] = [];
        const indices: number[] = [];
        const ringCount = railPoints.length;
        const profileCount = profilePoints.length;

        railPoints.forEach((rail) => {
            profilePoints.forEach((p) => {
                positions.push(
                    rail[0] + (p[0] - cx),
                    rail[1] + (p[1] - cy),
                    (rail[2] ?? 0) + ((p[2] ?? 0) - cz)
                );
            });
        });

        for (let i = 0; i < ringCount - 1; i++) {
            for (let j = 0; j < profileCount; j++) {
                const jn = (j + 1) % profileCount;
                const a = i * profileCount + j;
                const b = i * profileCount + jn;
                const c = (i + 1) * profileCount + j;
                const d = (i + 1) * profileCount + jn;
                indices.push(a, c, b);
                indices.push(b, c, d);
            }
        }

        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geo.setIndex(indices);
        geo.computeVertexNormals();
        return geo;
    }, [data]);

    if (!geometry) return null;
    return (
        <mesh geometry={geometry} onClick={(e) => { e.stopPropagation(); onClick(e); }}>
            <meshStandardMaterial color={color} side={THREE.DoubleSide} />
        </mesh>
    );
};

/**
 * Revolve Mesh Component (EntityLayer case 'revolve').
 *
 * Spins the profile polyline around the picked axis using a real rotation
 * frame (no Y-axis assumption): each profile point rotates about the
 * axisStart→axisEnd line through startAngle..endAngle in `segments` steps.
 */
export const RevolveMesh: React.FC<{
    data: RevolveData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: (e: ThreeEvent<MouseEvent>) => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#44aa88';

    const geometry = useMemo(() => {
        const pts = data.profilePoints;
        if (pts.length < 2) return null;
        const axisStart = new THREE.Vector3(...(data.axisStart as [number, number, number]));
        const axisEnd = new THREE.Vector3(...(data.axisEnd as [number, number, number]));
        const axis = axisEnd.clone().sub(axisStart);
        if (axis.lengthSq() < 1e-8) return null;
        axis.normalize();

        const start = data.startAngle ?? 0;
        const end = data.endAngle ?? Math.PI * 2;
        const segments = Math.max(data.segments ?? 32, 3);
        const positions: number[] = [];
        const indices: number[] = [];

        for (let i = 0; i <= segments; i++) {
            const angle = start + (end - start) * (i / segments);
            const q = new THREE.Quaternion().setFromAxisAngle(axis, angle);
            pts.forEach((p) => {
                const v = new THREE.Vector3(p[0], p[1], p[2] ?? 0).sub(axisStart).applyQuaternion(q).add(axisStart);
                positions.push(v.x, v.y, v.z);
            });
        }
        const n = pts.length;
        const fullLoop = Math.abs(end - start) >= Math.PI * 2 - 1e-6;
        for (let i = 0; i < segments; i++) {
            for (let j = 0; j < n - 1; j++) {
                const a = i * n + j;
                const b = a + 1;
                const c = a + n;
                const d = c + 1;
                indices.push(a, c, b);
                indices.push(b, c, d);
            }
        }
        if (fullLoop) {
            // Close the seam between the last and first ring.
            for (let j = 0; j < n - 1; j++) {
                const a = segments * n + j;
                indices.push(a, a + 1, j);
                indices.push(a + 1, j + 1, j);
            }
        }

        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geo.setIndex(indices);
        geo.computeVertexNormals();
        return geo;
    }, [data]);

    if (!geometry) return null;
    return (
        <mesh geometry={geometry} onClick={(e) => { e.stopPropagation(); onClick(e); }}>
            <meshStandardMaterial color={color} side={THREE.DoubleSide} />
        </mesh>
    );
};

/**
 * Extrude Mesh Component
 */
export const ExtrudeMesh: React.FC<{
    data: ExtrudeData;
    isSelected: boolean;
    isHovered: boolean;
    onClick: (e: ThreeEvent<MouseEvent>) => void;
}> = ({ data, isSelected, isHovered, onClick }) => {
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : '#66aa66';

    const geometry = useMemo(() => {
        if (data.profilePoints.length < 3) return null;

        // Create shape from profile points in the XY ground plane (Z-up);
        // ExtrudeGeometry runs along +Z, which IS the level axis now, so the
        // mesh needs no corrective rotation.
        const shape = new THREE.Shape();
        const first = data.profilePoints[0];
        shape.moveTo(first[0], first[1]);

        for (let i = 1; i < data.profilePoints.length; i++) {
            const pt = data.profilePoints[i];
            shape.lineTo(pt[0], pt[1]);
        }

        const extrudeSettings = {
            depth: data.distance,
            bevelEnabled: false,
        };

        return new THREE.ExtrudeGeometry(shape, extrudeSettings);
    }, [data]);

    if (!geometry) return null;

    return (
        <mesh
            geometry={geometry}
            onClick={(e) => { e.stopPropagation(); onClick(e); }}
        >
            <meshStandardMaterial color={color} side={THREE.DoubleSide} />
        </mesh>
    );
};
